import { useEffect, useMemo, useRef } from "react";
import { TAdminUser } from "../../../../models/superadmin";
import { highlightText } from "../../../../utils/highlightText";
import CustomButton from "../../../UI/Buttons/CustomButton";
import VerticalDots from "../../../UI/Icons/VerticalDots";
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from "../../../../store";
import Roles from "../../../../models/roles";
import EditPencil from "../../../UI/Icons/EditPencil";
import Block from "../../../UI/Icons/Block";
import Mail from "../../../UI/Icons/Mail";
import { otherRequestsThunk } from "../../../../store/superadmin/thunks";
import { isFulfilled } from "@reduxjs/toolkit";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import { hideTransparentPreloaderAction, showNotificationAction, showTransparentPreloaderAction } from "../../../../store/application/slice";

// Define the props for the UserItem component
interface UserItemProps {
  admin: TAdminUser; // The admin user data to be displayed
  searchInput: string; // The search query to highlight matching text
  isOpen: boolean; // A flag to check if the dropdown is open or closed
  onToggle: () => void; // A function to toggle the dropdown visibility
  onEdit: () => void; // A function to trigger the edit action
  total: number;
  getAllUsers: () => Promise<void>;
}

export default function UserItem({
  admin,
  searchInput,
  isOpen,
  onToggle,
  onEdit,
  total,
  getAllUsers
}: UserItemProps) {
  const dispatch = useDispatch();
  const dropdownRef = useRef<HTMLDivElement | null>(null); // Ref to the dropdown menu for detecting click outside

  const user = useSelector((state) => state.auth.user);

  const isGeneralAdmin = useMemo(
    () => user?.role.name === Roles.GENERAL_ADMIN,
    [user]
  );

  // Effect to handle clicks outside the dropdown to close it
  useEffect(() => {
    // Function to handle click outside dropdown
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) // Check if the click is outside the dropdown
      ) {
        isOpen && onToggle(); // Close the dropdown if it's open
      }
    };

    // Add event listener to handle outside clicks
    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup event listener on component unmount
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]); // Dependency on isOpen to rerun effect when dropdown state changes

  const makeUserUpdate = async (id: string, action: string) => {
    const payload = {
      user_uuid: id,
      action: action
    }
    isOpen && onToggle()
    dispatch(showTransparentPreloaderAction())
    const result = await dispatch(otherRequestsThunk(payload))
    if(isFulfilled(result)){
      dispatch(hideTransparentPreloaderAction())
      await getAllUsers();
      if(action === "resend_invite") {
        dispatch(showNotificationAction('Invite Resent'))
      } else if (action === "cancel_invite") {
        dispatch(showNotificationAction('Invite Cancelled'))
      } else if(action === "make_active") {
        dispatch(showNotificationAction('User Activated'))
      } else if(action === "make_inactive") {
        dispatch(showNotificationAction('User Inactivated'))
      }
    }
  }

  return (
    <div
      className={`bg-white p-15px d-flex gap-20 align-items-start align-items-lg-center justify-content-between ${
        total > 1 ? "border-bottom border-off-white" : ""
      } fs-13 fw-400 text-black`}
    >
      {/* User info section: name, email, phone number */}
      <div className={`d-flex flex-column flex-lg-row gap-20 gap-mobile-10 align-items-start align-items-lg-center text-break ${admin?.status === "active" ? "text-black" : "text-grey-light"}`}>
        <div className="min-w-200px w-200px fs-16 fw-700">
          {highlightText(admin?.first_name ?? "", searchInput)}{" "}
          {highlightText(admin?.last_name ?? "", searchInput)}
        </div>
        <div className="min-w-200px w-200px">
          {highlightText(admin?.email ?? "", searchInput)}
        </div>
        <div className="min-w-120px">
          {highlightText(admin?.phone_number ?? "", searchInput)}
        </div>
        <div className="min-w-120px">
          {admin.email_verified_at === null && admin.status === "active" ? (
            <span className="text-teal-dark fs-13 fw-700">Invitation Pending</span>
          ) : admin.status === "inactive" ? (
            <span className="fs-13 fw-700">Inactive</span>
          ) : (
            ""
          )}
        </div>
      </div>
      {!isGeneralAdmin ? (
        <div className="position-relative">
          {/* Custom button that triggers dropdown */}
          <CustomButton
            variant="primary"
            className="btn-theme bg-white p-0 h-auto"
            onClick={onToggle}
          >
            <VerticalDots />
          </CustomButton>
          {isOpen && (
            <div
              className="position-absolute end-0 d-flex flex-column dropdown-menu"
              ref={dropdownRef} // Attach ref to handle click outside detection
            >
              { admin.email_verified_at !== null && admin.status === "active" ? (
                <>
                  <div
                    className="dropdown-item cursor-pointer"
                    onClick={() => {
                      onToggle(); // Close the dropdown
                      setTimeout(() => {
                        onEdit(); // Then trigger edit
                      }, 0); // Delay by 1 tick to ensure dropdown closes
                    }}
                  >
                    <EditPencil />
                    Edit User
                  </div>
                  <div className="dropdown-item cursor-pointer text-nowrap" onClick={() => makeUserUpdate(admin.uuid, 'make_inactive')}>
                    <Block />
                    Make Inactive
                  </div>
                  
                </>
              ) : admin.email_verified_at === null && admin.status === "active" ? (
                <>
                  <div className="dropdown-item cursor-pointer text-nowrap" onClick={() => makeUserUpdate(admin.uuid, 'resend_invite')}>
                    <Mail />
                    Resend Invite
                  </div>
                  <div className="dropdown-item cursor-pointer text-nowrap" onClick={() => makeUserUpdate(admin.uuid, 'cancel_invite')}>
                    <Block />
                    Cancel Invite
                  </div>
                </>
              ) : admin.status === "inactive" ? (
                <>
                  <div className="dropdown-item cursor-pointer text-nowrap" onClick={() => makeUserUpdate(admin.uuid, 'make_active')}>
                    <RoundedTick />
                    Make Active
                  </div>
                </>
              ) : "" }
            </div>
          )}
        </div>
      ) : (
        ""
      )}
    </div>
  );
}
